// Two-state segmented control flipping the milestone table between Grouped (triage) and Sequential (numerical) layouts.

import { clsx } from "clsx";
import { LayoutList, ListOrdered } from "lucide-react";

export type TableView = "grouped" | "sequential";

export interface TableViewToggleProps {
  value: TableView;
  onChange: (next: TableView) => void;
  /** Override the radiogroup aria-label so the same component can serve
   *  more than one tracking surface. Defaults to "Milestone table view"
   *  for back-compat with the milestone callsite. */
  ariaLabel?: string;
}

const OPTIONS: ReadonlyArray<{
  value: TableView;
  label: string;
  Icon: typeof LayoutList;
}> = [
  { value: "grouped", label: "Grouped", Icon: LayoutList },
  { value: "sequential", label: "Sequential", Icon: ListOrdered },
];

export default function TableViewToggle({
  value,
  onChange,
  ariaLabel = "Milestone table view",
}: TableViewToggleProps) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid="table-view-toggle"
      className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-800"
    >
      {OPTIONS.map(({ value: optionValue, label, Icon }) => {
        const active = value === optionValue;
        return (
          <button
            key={optionValue}
            type="button"
            role="radio"
            aria-checked={active}
            aria-pressed={active}
            data-testid={`table-view-${optionValue}`}
            onClick={() => onChange(optionValue)}
            className={clsx(
              "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-caption font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400",
              active
                ? "bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300"
                : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200",
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}
